import clsx from 'clsx';
import Image from 'next/image';
import {
  Shimmer,
  toBase64,
} from '../../components/utility/imageShimmer/ImageShimmer';
import type { ImageType } from '../../types.d/storeTypes';
import styles from './CategoryHeader.module.scss';

export interface CategoryHeaderProps {
  backgroundColor?: string;
  backgroundColorOnMobile?: boolean;
  description?: string;
  desktopImage?: ImageType;
  mobileImage?: ImageType;
  subtitle?: string;
  title?: string;
  theme?: 'light' | 'dark';
}

/**
 * Category Header Component
 *
 * @param backgroundColor - string - hex code for section background color
 * @param backgroundColorOnMobile - boolean - whether to display the background color on mobile
 * @param description - string - description text
 * @param desktopImage - ImageType
 * @param mobileImage - ImageType
 * @param subtitle - string - Subtitle text
 * @param title - string - Title text
 */
export const CategoryHeader = ({
  backgroundColor,
  backgroundColorOnMobile = true,
  description,
  desktopImage,
  mobileImage,
  subtitle,
  title,
  theme = 'dark',
}: CategoryHeaderProps) => {
  const color = theme === 'light' ? 'var(--color-text-light)' : '';

  const containerStyles = {
    '--bg-mobile': backgroundColorOnMobile ? backgroundColor : '',
    '--bg-desktop': backgroundColor,
    '--text-mobile': backgroundColorOnMobile ? color : '',
    '--text-desktop': color,
  } as React.CSSProperties;

  return (
    <section className={styles.categoryHeader} style={containerStyles}>
      <div className={styles.categoryHeader__container}>
        <div className={clsx('grid no-gap', styles.categoryHeader__grid)}>
          {mobileImage && mobileImage.url && (
            <div
              className={clsx(
                'col xs4 l8-12',
                styles.categoryHeader__imageContainer,
                styles['categoryHeader__imageContainer--mobile']
              )}
            >
              <Image
                className={styles.categoryHeader__image}
                src={mobileImage.url}
                alt={mobileImage.title}
                title={mobileImage.title}
                fill
                sizes="100vw"
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  Shimmer(mobileImage.width, mobileImage.height)
                )}`}
              />
            </div>
          )}

          {desktopImage && desktopImage.url && (
            <div
              className={clsx(
                'col xs4 l7-12',
                styles.categoryHeader__imageContainer,
                styles['categoryHeader__imageContainer--desktop']
              )}
            >
              <Image
                className={styles.categoryHeader__image}
                src={desktopImage.url}
                alt={desktopImage.title}
                title={desktopImage.title}
                fill
                sizes="50vw"
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  Shimmer(desktopImage.width, desktopImage.height)
                )}`}
              />
            </div>
          )}

          <div className={clsx('col xs4 l1-6', styles.categoryHeader__content)}>
            <div className={styles.categoryHeader__contentTextWrapper}>
              <hgroup
                className={styles.categoryHeader__contentTextReverseTitle}
              >
                {subtitle && (
                  <h2
                    className={clsx(
                      'text-utility-utility-small-mobile text-utility-utility-desktop',
                      styles.categoryHeader__subtitle
                    )}
                  >
                    {subtitle}
                  </h2>
                )}
                {title && (
                  <h1
                    className={clsx(
                      'text-heading-h1-mobile text-heading-h1-desktop',
                      styles.categoryHeader__title
                    )}
                  >
                    {title}
                  </h1>
                )}
              </hgroup>

              {description && (
                <p
                  className={clsx(
                    'text-heading-h4-mobile text-heading-h4-desktop',
                    styles.categoryHeader__description
                  )}
                >
                  {description}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
